import React from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Star, Calendar as CalendarIcon, Clock as ClockIcon } from "lucide-react";
import { BookingStepProps } from "../types";
import { DURATION_OPTIONS } from "../constants";
import { getTutorName, getTutorPhoto } from "../utils/tutorHelpers";
import { formatDateForDisplay } from "../utils/timeHelpers";
import { useTimezone } from "src/hooks/useTimezone";
import ScrollToTop from "src/components/ScrollToTop";

const BookingStepTwo: React.FC<BookingStepProps> = ({
  tutor,
  formData,
  onNext,
  onBack,
  isLoading,
}) => {
  const { detectedTimezone, getTimezoneDisplayInfo } = useTimezone();

  const getDurationLabel = (duration: string) => {
    const option = DURATION_OPTIONS.find(opt => opt.value === duration);
    return option?.label || duration;
  };

  const getTimezoneLabel = (timezoneValue: string) => {
    return getTimezoneDisplayInfo(timezoneValue).label;
  };

  return (
    <>
      <ScrollToTop />
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
          Review and Confirm
        </h1>
        <p className="mt-4 text-xl text-gray-500 max-w-2xl mx-auto">
          Please review your session details before confirming.
        </p>
      </div>

      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Session Details</CardTitle>
          <CardDescription>
            Your tutoring session with {getTutorName(tutor)}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="flex items-center">
            <img
              src={getTutorPhoto(tutor)}
              alt={getTutorName(tutor)}
              className="w-16 h-16 rounded-full object-cover mr-4"
            />
            <div>
              <h3 className="font-medium">{getTutorName(tutor)}</h3>
              <div className="flex items-center mt-1">
                <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                <span className="ml-1 text-sm font-medium">
                  {tutor.rating ? tutor.rating.toFixed(1) : "No rating yet"}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Date and Time</h3>
                <div className="flex items-center mt-1">
                  <CalendarIcon className="h-4 w-4 mr-2 text-gray-700" />
                  <span className="font-medium">
                    {formData.date ? formatDateForDisplay(formData.date) : "No date selected"}
                  </span>
                </div>
              </div>
              <div className="flex items-center mt-2">
                <ClockIcon className="h-4 w-4 mr-2 text-gray-700" />
                <div>
                  <span>
                    {formData.timeSlot}{" "}
                    {formData.timezone && formData.timezone !== detectedTimezone
                      ? `(${getTimezoneLabel(formData.timezone)})`
                      : ""}
                  </span>
                  <span className="mx-2">• Duration:</span>
                  <span className="font-medium">
                    {getDurationLabel(formData.duration)}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Topic</h3>
                <p className="font-medium">{formData.topic}</p>
              </div>

              {formData.notes && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Additional Notes</h3>
                  <p className="text-sm text-gray-700">{formData.notes}</p>
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span>Duration</span>
              <span>{getDurationLabel(formData.duration)}</span>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            className="button-gradient text-white"
            onClick={onNext}
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : "Continue"}
          </Button>
        </CardFooter>
      </Card>
    </>
  );
};

export default BookingStepTwo;